import { Component } from '@angular/core';
import { DwQueryFilterGroup, DwQueryHelper } from '@devwareapps/devware-cap';
import { FlightSchoolOrganizationEntity, FlightSchoolTypeAllItems, CheckrideRequestHomePageQueryEntity } from '../../../../meta-data/app-meta-data.service';
import { combineLatest } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { isHomeScheduleCheckride } from '../../../shared/models/checkride-card.model';
import { HomeCheckrideRepositoryService } from '../../../shared/services/home-checkride-repository.service';
import { AppPermissions } from '../../../../meta-data/app-permissions.enum';
import { SchoolDashboardComponent } from '../school-dashboard/school-dashboard.component';
import { SchoolTaskService } from '../../services/school-task-service.service';
import { SchoolRepositoryService } from '../../services/school-repository.service';
import { AdminManagerService } from '../../../shared/services/admin-manager.service';
import * as moment_ from 'moment';

const moment = moment_;

@Component({
  selector: 'app-school-home',
  templateUrl: './school-home.component.html',
  styles: [':host { display: block; }']
})
export class SchoolHomeComponent extends SchoolDashboardComponent {
  protected loadMetarData = false;
  kpiScopeFilters: DwQueryFilterGroup;
  expressionContext: any;
  showPlanOfActionActions: boolean;
  tileDrillDownRoute: string;
  isOdaSchool = false;
  examinerLabel = 'Examiner/Check Instructor';
  homeCheckrides: CheckrideRequestHomePageQueryEntity[] = [];
  checkridesLoading = false;
  checkridesError: string;

  constructor(
    schoolTaskService: SchoolTaskService,
    private homeSchoolRepository: SchoolRepositoryService,
    adminManagerService: AdminManagerService,
    private homeCheckrideRepository: HomeCheckrideRepositoryService
  ) {
    super(schoolTaskService, homeSchoolRepository, adminManagerService);
    const user = adminManagerService.getPilexosUser();
    this.showPlanOfActionActions = user.hasPermission(AppPermissions.planOfActionSchoolAccess);
    this.expressionContext = {
      currentUser: { SchoolFlightSchoolOrganizationId: user.flightSchoolOrganizationId || -1 }
    };
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.homeSchoolRepository.getSchoolFeatures()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(features => {
        this.isOdaSchool = features?.FlightSchoolTypeId === FlightSchoolTypeAllItems.ODA;
        this.examinerLabel = this.isOdaSchool ? 'Unit Member' : 'Examiner/Check Instructor';
        this.tileDrillDownRoute = this.isOdaSchool
          ? '/school-reports/pass-rates-oda'
          : '/school-reports/pass-rates';
      }, () => {
        this.errorMessage = 'School features could not be loaded. Please refresh to load the performance KPIs.';
      });
  }

  loadSchoolStats(): void {
    this.loading = true;
    this.checkridesError = null;
    const schoolId = this.expressionContext.currentUser.SchoolFlightSchoolOrganizationId;
    combineLatest([
      this.homeSchoolRepository.getCurrentFlightSchoolStats(false, false, true),
      this.homeCheckrideRepository.loadSchoolTaskCheckrides(schoolId)
    ]).pipe(
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(([school, checkrides]) => {
      if (!school) {
        this.errorMessage = 'Your school profile could not be loaded.';
        this.loading = false;
        return;
      }
      const today = moment().startOf('day');
      const scheduled = checkrides.filter(checkride => isHomeScheduleCheckride(checkride, today));
      // Lightweight rows provide counts immediately; cards remain hidden until enriched.
      this.homeCheckrides = scheduled;
      this.flightSchool = { ...school, CheckRideRequest: checkrides };
      this.buildSchoolTasks(this.flightSchool, this.showAll);
      this.onSchoolStatsLoaded(school);
      this.loading = false;
      this.checkridesLoading = scheduled.length > 0;
      this.homeCheckrideRepository.load('school', school.FlightSchoolOrganizationId,
        scheduled.map(checkride => checkride.CheckRideRequestId))
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe(details => {
          this.homeCheckrides = details;
          this.checkridesLoading = false;
        }, () => {
          this.checkridesLoading = false;
          this.checkridesError = 'Checkride details could not be loaded. Please refresh to try again.';
        });
    }, () => {
      this.errorMessage = 'The school dashboard could not be loaded. Please refresh to try again.';
      this.loading = false;
    });
  }

  protected onSchoolStatsLoaded(flightSchool: FlightSchoolOrganizationEntity): void {
    this.kpiScopeFilters = null;
    if (!flightSchool?.FlightSchoolOrganizationId) {
      return;
    }

    const filters = new DwQueryHelper().filters;
    const yearStart = moment().startOf('year');
    this.kpiScopeFilters = new DwQueryFilterGroup();
    this.kpiScopeFilters.AddFilterAnd(filters.Equal(
      'roCheckrideFactBase.FlightSchoolOrganizationId', flightSchool.FlightSchoolOrganizationId));
    this.kpiScopeFilters.AddFilterAnd(filters.GreaterThanEqual(
      'roCheckrideFactBase.CheckrideDateTime', yearStart.format('YYYY-MM-DD')));
    this.kpiScopeFilters.AddFilterAnd(filters.LessThan(
      'roCheckrideFactBase.CheckrideDateTime', yearStart.clone().add(1, 'year').format('YYYY-MM-DD')));
  }
}
